import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Row, Col, Card, Input, Select, Spin, Empty, Tag, Space,
  Typography, Button, Drawer, Descriptions, Image, Rate, Divider,
  Radio, Dropdown, Modal, List, message, Avatar, Form,
} from 'antd';
import {
  HomeOutlined, SearchOutlined, ReloadOutlined,
  EnvironmentOutlined,
  GlobalOutlined, AimOutlined, ThunderboltOutlined, DownOutlined,
  PlusOutlined, TeamOutlined,
} from '@ant-design/icons';
import { officeAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;

const isCoworking = (o) => (o && o.property_type === 'coworking');
const STAGE_LABEL = (t) => ({
  new: '🆕 ' + t('office.stage.new', 'New Inquiry'), contacted: '📞 ' + t('office.stage.contacted', 'Contacted'), viewing: '👀 ' + t('office.stage.viewing', 'Viewing'),
  negotiating: '🤝 ' + t('office.stage.negotiating', 'Negotiating'), closed_won: '🏆 ' + t('office.stage.closedWon', 'Closed (Won)'), closed_lost: '❌ ' + t('office.stage.closedLost', 'Closed (Lost)'),
});

const fmtPrice = (p) => {
  if (!p) return '-';
  if (p >= 1000000) return `฿${(p / 1000000).toFixed(1)}M`;
  if (p >= 1000) return `฿${(p / 1000).toFixed(0)}K`;
  return `฿${p.toLocaleString()}`;
};

// 1 ping = 3.305785 sqm
const SQM_PER_PING = 3.305785;
const toPing = (sqm) => (sqm ? sqm / SQM_PER_PING : null);
const fmtPing = (sqm) => {
  const p = toPing(sqm);
  return p ? `${p.toFixed(1)} ping` : '-';
};
const fmtPerPing = (o) => {
  if (!o.price || !o.area_sqm) return null;
  return Math.round(o.price / (o.area_sqm / SQM_PER_PING));
};

const buildGMap = (o) => {
  const parts = [o.address, o.district, o.city, o.country].filter(Boolean);
  if (!parts.length) return null;
  const out = [];
  parts.forEach(p => {
    const tokens = p.split(',').map(t => t.trim()).filter(Boolean);
    const already = tokens.some(t => out.join(' ').toLowerCase().includes(t.toLowerCase()));
    if (!already) out.push(p);
  });
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(out.join(', '))}`;
};

const POI_ICONS = {
  tennis: '🎾', pickleball: '🏓', mall: '🏬', shopping_street: '🛍️',
  park: '🌳', gym: '💪', golf: '⛳', fine_dining: '🍽️', convention: '🏛️',
};

function OfficeCard({ o, onOpen, lineChatUrl, onAddCRM }) {
  const { t } = useTranslation();
  return (
    <Card
      hoverable
      onClick={() => onOpen(o)}
      cover={
        o.images && o.images.length > 0 ? (
          <img
            alt={o.title}
            src={o.images[0]}
            style={{ height: 180, objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{ height: 180, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HomeOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          </div>
        )
      }
    >
      <Title level={5} style={{ margin: 0, minHeight: 44, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {o.title}
      </Title>
      <div style={{ marginTop: 4 }}>
        {isCoworking(o) ? <Tag color="purple">{t('office.coworking', 'Coworking')}</Tag> : <Tag color="blue">{t('office.office', 'Office')}</Tag>}
      </div>
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ color: '#e8363a', fontSize: 16 }}>
          {fmtPrice(o.price)}<Text type="secondary" style={{ fontSize: 12 }}> {isCoworking(o) ? '/' + t('office.sqmMonth', 'sqm/mo') : '/' + t('office.month', 'month')}</Text>
        </Text>
      </div>
      <div style={{ marginTop: 2 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {fmtPing(o.area_sqm)} · {t('office.perPing', 'per ping')} {fmtPerPing(o) ? fmtPrice(fmtPerPing(o)) : '-'}
        </Text>
      </div>
      <div style={{ marginTop: 6 }}>
        {o.area_sqm && <Tag>{o.area_sqm} sqm</Tag>}
        {o.building_name && <Tag color="blue">{o.building_name.split('/')[0].trim()}</Tag>}
      </div>
      {o.district && (
        <div style={{ marginTop: 4 }}>
          <EnvironmentOutlined style={{ color: '#1677ff' }} /> <Text type="secondary">{o.district}</Text>
        </div>
      )}
      {o.rating_avg ? (
        <div style={{ marginTop: 4 }}>
          <Rate disabled allowHalf defaultValue={o.rating_avg} style={{ fontSize: 13 }} />
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>{o.rating_avg}</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 2 }}>（{o.rating_count} {t('office.ratings', 'ratings')}）</Text>
        </div>
      ) : (
        <div style={{ marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{t('office.noRating', 'No ratings yet')}</Text>
        </div>
      )}
      {o.nearby_transit && o.nearby_transit.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {o.nearby_transit.slice(0, 2).map((t, i) => (
            <Tag key={i} color={t.type === 'bts' ? 'green' : 'purple'} style={{ marginRight: 4 }}>
              {t.type.toUpperCase()} {t.name}
            </Tag>
          ))}
        </div>
      )}
      {o.nearby_pois && o.nearby_pois.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
          {[...new Set(o.nearby_pois.map(p => POI_ICONS[p.category] || '📍'))].slice(0, 5).join(' ')}
          {' '}<Text type="secondary" style={{ fontSize: 12 }}>{t('office.nearbyFacilities', 'Nearby Facilities')}</Text>
        </div>
      )}
      {o.nearby_hotels && o.nearby_hotels.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
          {[...new Set(o.nearby_hotels.map(h => h.type === 'airbnb' ? '\u{1F3E1}' : '\u{1F3E8}'))].join(' ')}
          {' '}<Text type="secondary" style={{ fontSize: 12 }}>{t('office.nearbyStays', 'Nearby Stays')} {o.nearby_hotels.length}</Text>
        </div>
      )}
      {lineChatUrl && (
        <Button
          type="primary"
          size="small"
          ghost
          icon={<span style={{ color: '#06c755' }}>💬</span>}
          href={lineChatUrl}
          target="_blank"
          rel="noreferrer"
          style={{ marginTop: 8, color: '#06c755', borderColor: '#06c755' }}
          onClick={e => e.stopPropagation()}
        >
          {t('office.lineInquiry', 'LINE Inquiry')}
        </Button>
      )}
      {onAddCRM && (
        <Button
          size="small"
          icon={<TeamOutlined />}
          style={{ marginTop: 8, marginLeft: 8, color: '#722ed1', borderColor: '#722ed1' }}
          onClick={e => { e.stopPropagation(); onAddCRM(o); }}
        >
          {t('office.addToCRM', 'Add to CRM')}
        </Button>
      )}
    </Card>
  );
}

export default function OfficeSearchPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [areas, setAreas] = useState([]);
  const [poiCats, setPoiCats] = useState([]);
  const [sources, setSources] = useState([]);

  const [source, setSource] = useState([]);
  const [search, setSearch] = useState('');
  const [district, setDistrict] = useState(undefined);
  const [poiKeys, setPoiKeys] = useState([]);
  const [priceMax, setPriceMax] = useState(undefined);
  const [minRating, setMinRating] = useState(undefined);
  const [sortBy, setSortBy] = useState('price');
  const [sortOrder, setSortOrder] = useState('asc');

  const [viewProp, setViewProp] = useState(null);
  const [crmOpen, setCrmOpen] = useState(false);
  const [crmProp, setCrmProp] = useState(null);
  const [crmLeads, setCrmLeads] = useState([]);
  const [crmSearch, setCrmSearch] = useState('');
  const [crmAddMode, setCrmAddMode] = useState(false);
  const [crmForm] = Form.useForm();
  const [importing, setImporting] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [myRating, setMyRating] = useState(undefined);
  const [lineOaId, setLineOaId] = useState('JJProject');
  const [similarLoading, setSimilarLoading] = useState(false);

  const loadSimilar = useCallback(async () => {
    if (!viewProp || !viewProp.id) {
      setSimilar([]);
      return;
    }
    setSimilarLoading(true);
    try {
      const res = await officeAPI.similar(viewProp.id, 6);
      const d = res.data || {};
      setSimilar(d.items || []);
    } catch (e) {
      console.error('load similar failed', e);
      setSimilar([]);
    } finally {
      setSimilarLoading(false);
    }
  }, [viewProp]);

  useEffect(() => {
    loadSimilar();
  }, [loadSimilar]);

  const loadLineInfo = useCallback(async () => {
    try {
      const res = await officeAPI.getLineInfo();
      const d = res.data || {};
      if (d.line_oa_id) setLineOaId(d.line_oa_id);
    } catch (e) {
      console.error('load line info failed', e);
    }
  }, []);

  useEffect(() => { loadLineInfo(); }, [loadLineInfo]);

  const buildLineChatUrl = (o) => {
    if (!lineOaId) return null;
    // Basic ID (e.g. @408mhsym) is what line.me deep links accept — the @ stays literal in the path.
    // U-prefixed MID values are kept as-is for backward compat but deep links normally need the Basic ID.
    const oa = (lineOaId.startsWith('@') || lineOaId.startsWith('U')) ? lineOaId : `@${lineOaId}`;
    const area = o.district || '';
    const price = o.price ? `${fmtPrice(o.price)}/${t('office.month', 'month')}` : '';
    const text = `${t('office.lineMsgIntro', 'Hello! I would like to inquire about this office property:')}\n📍 ${o.title}\n🏢 ${area}\n💰 ${price}\n\n（${t('office.lineMsgRef', 'Property Ref#')}${o.id}）\n${t('office.lineMsgOutro', 'Please help arrange a viewing, thank you!')}`;
    return `https://line.me/R/oaMessage/${oa}/?text=${encodeURIComponent(text)}`;
  };

  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  useEffect(() => {
    if (focusId && items.length > 0) {
      const target = items.find(i => String(i.id) === focusId);
      if (target) {
        setViewProp(target);
      }
    }
  }, [focusId, items]);

  const loadSources = useCallback(async () => {
    try {
      const res = await officeAPI.getSources();
      const d = res.data || {};
      setSources(d.sources || []);
    } catch (e) {
      console.error('load sources failed', e);
    }
  }, []);

  const loadAreas = useCallback(async (src) => {
    try {
      const res = await officeAPI.getAreas(src);
      const d = res.data || {};
      setAreas((d.areas || []).filter(a => a.count > 0));
      setPoiCats(d.poi_categories || []);
    } catch (e) {
      console.error('load areas failed', e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, sort_by: sortBy, sort_order: sortOrder };
      if (source.length) params.source = source.join(',');
      if (search) params.search = search;
      if (district) params.district = district;
      if (poiKeys.length) params.poi = poiKeys.join(',');
      if (priceMax) params.price_max = priceMax;
      if (minRating) params.min_rating = minRating;
      const res = await officeAPI.list(params);
      setItems((res.data && res.data.items) || []);
      setTotal((res.data && res.data.total) || 0);
    } catch (e) {
      console.error('load offices failed', e);
    } finally {
      setLoading(false);
    }
  }, [source, search, district, poiKeys, priceMax, minRating, sortBy, sortOrder]);

  useEffect(() => {
    setMyRating(viewProp ? viewProp.my_rating : undefined);
  }, [viewProp]);

  const handleRate = useCallback(async (val) => {
    if (!viewProp || !val) return;
    setRatingSubmitting(true);
    try {
      const res = await officeAPI.rate(viewProp.id, val);
      const d = res.data || {};
      setMyRating(d.my_rating);
      setViewProp(prev => ({
        ...prev,
        rating_avg: d.rating_avg,
        rating_count: d.rating_count,
        my_rating: d.my_rating,
      }));
      load();
    } catch (e) {
      console.error('rate failed', e);
    } finally {
      setRatingSubmitting(false);
    }
  }, [viewProp, load]);

  useEffect(() => { loadSources(); }, [loadSources]);
  useEffect(() => { loadAreas(source.join(',')); }, [loadAreas, source]);
  useEffect(() => { load(); }, [load]);

  const handleImport = async (srcKey) => {
    setImporting(true);
    try {
      const res = await officeAPI.import({ source: srcKey, max_pages: 3, force: true });
      const d = res.data || {};
      if (d.rate_limited) {
        alert(`${t('office.rateLimited', 'Sync rate limit reached, please try again in')} ${d.next_fetch_in_hours} ${t('office.hours', 'hours')}`);
      } else {
        alert(`${t('office.syncDone', 'Sync complete:')} ${t('office.added', 'added')} ${d.added ?? 0}, ${t('office.updated', 'updated')} ${d.updated ?? 0}`);
      }
      loadSources();
      loadAreas(source.join(','));
      load();
    } catch (e) {
      alert(t('office.syncFailed', 'Sync failed:') + ' ' + (e.message || e));
    } finally {
      setImporting(false);
    }
  };

  const loadCrmLeads = useCallback(async (kw) => {
    const q = new URLSearchParams({ limit: 100 });
    if (kw) q.append('search', kw);
    const d = await fetch(`/api/v1/house/office/crm/leads?${q}`, { headers: { Authorization: 'Bearer ' + localStorage.getItem('spo_auth_token'), 'Content-Type': 'application/json' } }).then(r => r.json());
    setCrmLeads(d.leads || []);
  }, []);

  const openAddCRM = (o) => {
    setCrmProp(o);
    setCrmSearch('');
    setCrmAddMode(false);
    setCrmOpen(true);
    loadCrmLeads('');
  };

  const addPropToLead = async (leadId) => {
    await fetch(`/api/v1/house/office/crm/leads/${leadId}/properties?property_id=${crmProp.id}&status=interested`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('spo_auth_token'), 'Content-Type': 'application/json' },
    });
    message.success(`${t('office.addedToCase', 'Added to case')}: ${crmProp.title}`);
    setCrmOpen(false);
  };

  const createLeadWithProp = async () => {
    const vals = await crmForm.validateFields();
    const res = await fetch('/api/v1/house/office/crm/leads', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('spo_auth_token'), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...vals,
        budget_min: vals.budget_min ? Number(vals.budget_min) : null,
        budget_max: vals.budget_max ? Number(vals.budget_max) : null,
        preferred_locations: vals.preferred_locations ? vals.preferred_locations.split(',').map(s => s.trim()).filter(Boolean) : [],
        property_ids: [crmProp.id],
        source: 'office_search',
        stage: 'contacted',
      }),
    }).then(r => r.json());
    if (res.id) {
      message.success(`${t('office.caseCreated', 'Case created & property linked')}: ${crmProp.title}`);
      setCrmOpen(false);
      crmForm.resetFields();
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <HomeOutlined style={{ color: '#1677ff', marginRight: 8 }} />
            {t('office.pageTitle', 'Bangkok Office Rental Search')}
          </Title>
          <Text type="secondary">{t('office.sourceLabel', 'Sources')}: {source.length ? sources.filter(s => source.includes(s.key)).map(s => s.name_zh).join(', ') : t('office.all', 'All')} · {t('office.totalLabel', 'Total')} {total} {t('office.officesRent', 'offices')}</Text>
          {source.includes('bangkok_coworking') && <Tag color="purple" style={{ marginLeft: 8 }}>{t('office.coworking', 'Coworking')}</Tag>}
        </Col>
        <Col>
          <Dropdown
            menu={{
              items: (sources.length ? sources : [{ key: 'bangkok_office_finder', name: 'Bangkok Office Finder', name_zh: t('office.sourceDefault', 'Bangkok Office Finder') }]).map(s => ({
                key: s.key,
                label: `${s.name_zh}（${s.count ?? 0}）`,
                onClick: () => handleImport(s.key),
              })),
            }}
          >
            <Button icon={<ReloadOutlined />} loading={importing}>
              {t('office.syncData', 'Sync Data')} <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap>
            <Select
              placeholder={t('office.sourcePlaceholder', 'Data Sources (multi-select)')}
              style={{ width: 240 }}
              mode="multiple"
              value={source}
              onChange={setSource}
              allowClear
              maxTagCount={2}
            >
              {sources.map(s => (
                <Option key={s.key} value={s.key}>
                  {s.name_zh}（{s.count}）
                </Option>
              ))}
            </Select>
            <Input
              placeholder={t('office.searchPlaceholder', 'Search project or office name...')}
              prefix={<SearchOutlined />}
              style={{ width: 260 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
              onPressEnter={() => load()}
            />
            <Select
              placeholder={t('office.areaPlaceholder', 'Popular Bangkok Areas')}
              style={{ width: 220 }}
              value={district}
              onChange={setDistrict}
              allowClear
            >
              {areas.map(a => (
                <Option key={a.slug} value={a.name_en}>
                  {a.name_en}（{a.count}）
                </Option>
              ))}
            </Select>
            <Select
              placeholder={t('office.poiPlaceholder', 'Nearby Facilities')}
              style={{ width: 260 }}
              mode="multiple"
              value={poiKeys}
              onChange={setPoiKeys}
              maxTagCount={3}
            >
              {poiCats.map(c => (
                <Option key={c.key} value={c.key}>
                  {(POI_ICONS[c.key] || '📍')} {c.label_zh} / {c.label_en}
                </Option>
              ))}
            </Select>
            <Select
              style={{ width: 150 }}
              value={priceMax}
              onChange={setPriceMax}
              placeholder={t('office.budgetPlaceholder', 'Max Budget')}
              allowClear
            >
              <Option value={50000}>฿50K below</Option>
              <Option value={100000}>฿100K below</Option>
              <Option value={200000}>฿200K below</Option>
              <Option value={500000}>฿500K below</Option>
              <Option value={1000000}>฿1M below</Option>
            </Select>
            <Select
              style={{ width: 130 }}
              value={minRating}
              onChange={setMinRating}
              placeholder={t('office.ratingPlaceholder', 'Rating Filter')}
              allowClear
            >
              <Option value={4.5}>★ 4.5+</Option>
              <Option value={4.0}>★ 4.0+</Option>
              <Option value={3.5}>★ 3.5+</Option>
              <Option value={3.0}>★ 3.0+</Option>
            </Select>
            <Radio.Group value={sortBy} onChange={e => { setSortBy(e.target.value); setSortOrder('asc'); }} size="small">
              <Radio.Button value="price">{t('office.sortPrice', 'Price Low→High')}</Radio.Button>
              <Radio.Button value="area_sqm">{t('office.sortArea', 'Area')}</Radio.Button>
              <Radio.Button value="price_per_ping">{t('office.sortPerPing', 'Price / Ping')}</Radio.Button>
              <Radio.Button value="rating">{t('office.sortRating', 'Rating')}</Radio.Button>
            </Radio.Group>
            <Button type="primary" icon={<SearchOutlined />} onClick={load}>{t('common.search', 'Search')}</Button>
          </Space>

          {areas.length > 0 && (
            <div>
              <Text type="secondary">{t('office.hotAreas', 'Popular Areas:')}</Text>
              <Space wrap size={4} style={{ marginTop: 4 }}>
                {areas.slice(0, 10).map(a => (
                  <Tag
                    key={a.slug}
                    color={district === a.name_en ? 'blue' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setDistrict(district === a.name_en ? undefined : a.name_en)}
                  >
                    {a.name_en}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
        </Space>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <Empty description={t('office.noResults', 'No matching offices')} style={{ padding: 40 }} />
      ) : (
        <Row gutter={[16, 16]}>
          {items.map(o => (
            <Col xs={24} sm={12} md={8} lg={6} key={o.id}>
              <OfficeCard o={o} onOpen={setViewProp} lineChatUrl={buildLineChatUrl(o)} onAddCRM={openAddCRM} />
            </Col>
          ))}
        </Row>
      )}

      <Drawer
        title={<span><HomeOutlined /> {t('office.detailTitle', 'Office Details')}</span>}
        width={620}
        open={!!viewProp}
        onClose={() => setViewProp(null)}
      >
        {viewProp && (
          <div>
            <Title level={4} style={{ marginTop: 0 }}>{viewProp.title}</Title>

            <Space style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              {buildGMap(viewProp) && (
                <Button
                  type="primary"
                  icon={<EnvironmentOutlined />}
                  href={buildGMap(viewProp)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('office.openGoogleMaps', 'Open Google Maps')}
                </Button>
              )}
              {buildLineChatUrl(viewProp) && (
                <Button
                  type="primary"
                  style={{ background: '#06c755', borderColor: '#06c755' }}
                  icon={<span>💬</span>}
                  href={buildLineChatUrl(viewProp)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('office.lineInquiry', 'LINE Inquiry')}
                </Button>
              )}
              <Button
                type="primary"
                style={{ background: '#722ed1', borderColor: '#722ed1' }}
                icon={<TeamOutlined />}
                onClick={() => openAddCRM(viewProp)}
              >
                {t('office.addToCRM', 'Add to CRM')}
              </Button>
              {viewProp.rating_avg ? (
                <Text type="secondary">
                  <Rate disabled allowHalf defaultValue={viewProp.rating_avg} style={{ fontSize: 14 }} />
                  <Text strong style={{ marginLeft: 4 }}>{viewProp.rating_avg}</Text>
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 2 }}>（{viewProp.rating_count} {t('office.ratings', 'ratings')}）</Text>
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>{t('office.noRating', 'No ratings yet')}</Text>
              )}
              <Divider type="vertical" />
              <Text type="secondary" style={{ fontSize: 13 }}>{t('office.myRating', 'My Rating:')}</Text>
              <Rate
                allowHalf={false}
                value={myRating || 0}
                onChange={handleRate}
                disabled={ratingSubmitting}
                style={{ fontSize: 18 }}
              />
              {myRating ? (
                <Button type="link" size="small" style={{ fontSize: 12 }} onClick={() => handleRate(0)}>
                  {t('office.clearRating', 'Clear Rating')}
                </Button>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>{t('office.clickStars', 'Click stars to rate')}</Text>
              )}
              {viewProp.source_url && (
                <Button
                  icon={<GlobalOutlined />}
                  href={viewProp.source_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('office.viewSourcePhotos', 'View Original Photos')}
                </Button>
              )}
            </Space>

            <Space size={8} style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              {viewProp.property_type === 'coworking' ? <Tag color="purple">{t('office.coworking', 'Coworking')}</Tag> : <Tag color="blue">{t('office.office', 'Office')}</Tag>}
              <Tag color="green">{t('office.lease', 'Lease')}</Tag>
              {viewProp.district && <Tag color="orange">{viewProp.district}</Tag>}
            </Space>

            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('office.monthlyRent', 'Monthly Rent')}>
                <Text strong style={{ color: '#e8363a', fontSize: 16 }}>{fmtPrice(viewProp.price)} THB {isCoworking(viewProp) ? '/' + t('office.sqmMonth', 'sqm/mo') : '/' + t('office.month', 'month')}</Text>
                {!isCoworking(viewProp) && viewProp.price_per_sqm && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>（{viewProp.price_per_sqm.toLocaleString()} THB/sqm）</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('office.areaLabel', 'Area (sqm / ping)')}>
                {viewProp.area_sqm ? `${viewProp.area_sqm} sqm` : '-'}
                {toPing(viewProp.area_sqm) && (
                  <Text type="secondary" style={{ marginLeft: 6 }}>
                    = {toPing(viewProp.area_sqm).toFixed(1)} {t('office.ping', 'ping')}
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('office.pricePerPing', 'Price / Ping')}>
                {fmtPerPing(viewProp) ? `${fmtPerPing(viewProp).toLocaleString()} THB / ${t('office.ping', 'ping')}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('office.building', 'Building')}>{viewProp.building_name || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('office.district', 'District')}>
                {buildGMap(viewProp) ? (
                  <a href={buildGMap(viewProp)} target="_blank" rel="noreferrer">
                    <EnvironmentOutlined style={{ color: '#1677ff' }} /> {[viewProp.district, viewProp.city, viewProp.country].filter(Boolean).join(', ')}
                  </a>
                ) : ([viewProp.district, viewProp.city, viewProp.country].filter(Boolean).join(', ') || '-')}
              </Descriptions.Item>
              <Descriptions.Item label={t('office.address', 'Address')}>
                {viewProp.address ? (
                  <a href={buildGMap(viewProp) || '#'} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                    <AimOutlined /> {viewProp.address}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('office.transit', 'Transit')}>
                {viewProp.nearby_transit && viewProp.nearby_transit.length > 0
                  ? viewProp.nearby_transit.map((t, i) => (
                      <Tag key={i} color={t.type === 'bts' ? 'green' : 'purple'}>
                        {t.type.toUpperCase()} {t.name}
                      </Tag>
                    ))
                  : '-'}
              </Descriptions.Item>
              {viewProp.description && (
                <Descriptions.Item label={t('office.description', 'Description')}>
                  <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{viewProp.description}</Paragraph>
                </Descriptions.Item>
              )}
            </Descriptions>

                        {viewProp.nearby_pois && viewProp.nearby_pois.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}><ThunderboltOutlined style={{ color: '#fa8c16' }} /> {t('office.nearbyFacilities', 'Nearby Facilities')}</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>{t('office.poiHint', 'Click a card to view its location on Google Maps and photos')}</Text>
                <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                  {viewProp.nearby_pois.map((p, i) => (
                    <Col xs={12} md={8} key={i}>
                      <a href={p.link} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none' }}>
                        <Card
                          size="small"
                          hoverable
                          cover={
                            p.photo ? (
                              <img
                                alt={p.name}
                                src={p.photo}
                                style={{ height: 110, objectFit: 'cover', borderRadius: '6px 6px 0 0' }}
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div style={{ height: 110, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px 6px 0 0' }}>
                                <span style={{ fontSize: 40 }}>{POI_ICONS[p.category] || '📍'}</span>
                              </div>
                            )
                          }
                        >
                          <Space align="start" style={{ width: '100%' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text strong style={{ fontSize: 13 }}>{p.name}</Text>
                              <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>{p.category_zh} · {t('office.approx', 'approx')} {p.distance_km} km</Text>
                              </div>
                              <div style={{ marginTop: 2 }}>
                                <Text style={{ fontSize: 12, color: '#1677ff' }}>
                                  <GlobalOutlined /> {t('office.googleMaps', 'Google Maps')}
                                </Text>
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </a>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {viewProp.nearby_hotels && viewProp.nearby_hotels.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}><HomeOutlined style={{ color: '#722ed1' }} /> {t('office.nearbyStayTitle', 'Nearby Stay Experience (Hotels & Airbnb)')}</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('office.nearbyStayHint', 'Stay nearby to experience the neighborhood before choosing an office')}
                </Text>
                <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                  {viewProp.nearby_hotels.map((h, i) => (
                    <Col xs={24} md={12} key={i}>
                      <Card size="small">
                        <Space align="start">
                          <span style={{ fontSize: 22 }}>{h.type === 'airbnb' ? '\u{1F3E1}' : '\u{1F3E8}'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text strong style={{ fontSize: 13 }}>{h.name}</Text>
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {h.type === 'airbnb' ? 'Airbnb' : t('office.hotel', 'Hotel')} · {t('office.approx', 'approx')} {h.distance_km} km · {h.price}
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: 12 }}>{h.note_zh}</Text>
                            </div>
                            <Space size="middle" style={{ marginTop: 4 }}>
                              <span style={{ fontSize: 12, color: '#888' }}>
                                <Rate disabled allowHalf defaultValue={h.rating} style={{ fontSize: 12 }} />
                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>{h.rating}</Text>
                              </span>
                              <a href={h.link} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                                {t('office.viewDetails', 'View Details')} <GlobalOutlined />
                              </a>
                            </Space>
                          </div>
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {viewProp.images && viewProp.images.length > 0 && (
              <div>
                <Title level={5}>{t('office.photos', 'Photos')}（{viewProp.images.length}）</Title>
                <Image.PreviewGroup>
                  <Row gutter={[8, 8]}>
                    {viewProp.images.map((img, i) => (
                      <Col span={8} key={i}>
                        <Image
                          src={img}
                          alt=""
                          style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 6 }}
                          fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><rect width='100' height='100' fill='%23f0f0f0'/></svg>"
                        />
                      </Col>
                    ))}
                  </Row>
                </Image.PreviewGroup>
              </div>
            )}
          {similar.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Title level={5}><AimOutlined style={{ color: '#1677ff' }} /> {t('office.similarProps', 'Similar Properties')}（{similar.length}）{similarLoading && <Spin size="small" style={{ marginLeft: 8 }} />}</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('office.similarHint', 'Other options in the same area with similar price and size')}
                </Text>
                <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                  {similar.map(s => (
                    <Col xs={24} key={s.id}>
                      <Card size="small" hoverable onClick={() => setViewProp(s)}>
                        <Space align="center">
                          {s.images && s.images.length > 0 ? (
                            <img
                              src={s.images[0]}
                              alt=""
                              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4 }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div style={{ width: 64, height: 48, background: '#f5f5f5', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <HomeOutlined style={{ color: '#d9d9d9' }} />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text strong style={{ fontSize: 13 }}>{s.title}</Text>
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {s.district || ''} · {fmtPing(s.area_sqm)} · {t('office.perPing', 'per ping')} {fmtPerPing(s) ? fmtPrice(fmtPerPing(s)) : '-'}
                              </Text>
                            </div>
                            <div>
                              <Text strong style={{ color: '#e8363a', fontSize: 13 }}>{fmtPrice(s.price)}</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}> / month</Text>
                              {s.similar_reasons && s.similar_reasons.length > 0 && (
                                <span style={{ marginLeft: 8 }}>
                                  {s.similar_reasons.map((r, i) => (
                                    <Tag key={i} color="blue" style={{ marginRight: 4, fontSize: 11 }}>{r}</Tag>
                                  ))}
                                </span>
                              )}
                            </div>
                          </div>
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* ── Add to CRM Modal ── */}
      <Modal
        title={<span><TeamOutlined /> {t('office.addToCRM', 'Add to CRM')} — {crmProp?.title}</span>}
        open={crmOpen}
        onCancel={() => setCrmOpen(false)}
        footer={null}
        width={640}
      >
        {!crmAddMode ? (
          <>
            <Space style={{ marginBottom: 8, width: '100%' }}>
              <Input
                prefix={<SearchOutlined />}
                value={crmSearch}
                onChange={e => { setCrmSearch(e.target.value); loadCrmLeads(e.target.value); }}
                placeholder={t('office.searchLeads', 'Search existing cases (company / contact)')}
                allowClear
                style={{ flex: 1 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCrmAddMode(true)}>{t('office.newCase', 'New Case')}</Button>
            </Space>
            <List
              size="small"
              dataSource={crmLeads}
              style={{ maxHeight: 360, overflow: 'auto' }}
              locale={{ emptyText: t('office.noLeads', 'No cases yet, click New Case to start') }}
              renderItem={(lead) => (
                <List.Item actions={[
                  <Button key="add" size="small" type="primary" onClick={() => addPropToLead(lead.id)}>{t('office.addToThisCase', 'Add to This Case')}</Button>,
                ]}>
                  <Space>
                    <Avatar size={28} style={{ background: '#722ed1' }}>{(lead.company_name || lead.name || '?')[0]}</Avatar>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>{lead.company_name || lead.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {lead.stage === 'closed_won' ? '🏆 ' + t('office.stage.closedWon', 'Closed (Won)') : lead.stage === 'closed_lost' ? '❌ ' + t('office.stage.closedLost', 'Closed (Lost)') : STAGE_LABEL(t)[lead.stage] || lead.stage}
                        {' · '}{lead.preferred_locations?.join(', ') || t('office.noArea', 'No area set')} · {t('office.score', 'Score')} {lead.lead_score || 0}
                      </Text>
                    </div>
                  </Space>
                </List.Item>
              )}
            />
          </>
        ) : (
          <Form form={crmForm} layout="vertical" size="small" initialValues={{ language_pref: 'zh', source: 'office_search', stage: 'contacted' }}>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="company_name" label={t('office.companyName', 'Company Name')}><Input placeholder="e.g. Ulink" /></Form.Item></Col>
              <Col span={12}><Form.Item name="name" label={t('office.contact', 'Contact')} rules={[{ required: true }]}><Input /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}><Form.Item name="phone" label={t('office.phone', 'Phone')}><Input /></Form.Item></Col>
              <Col span={8}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
              <Col span={8}><Form.Item name="office_type_pref" label={t('office.officeType', 'Office Type')}>
                <Select allowClear><Select.Option value="office">🏢 {t('office.office', 'Office')}</Select.Option><Select.Option value="coworking">🧑‍💻 {t('office.coworking', 'Coworking')}</Select.Option><Select.Option value="serviced">🛎️ {t('office.serviced', 'Serviced')}</Select.Option></Select>
              </Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}><Form.Item name="seats_needed" label={t('office.seatsNeeded', 'Seats Needed')}><Input type="number" /></Form.Item></Col>
              <Col span={8}><Form.Item name="budget_min" label={t('office.budgetMin', 'Budget Min')}><Input type="number" /></Form.Item></Col>
              <Col span={8}><Form.Item name="budget_max" label={t('office.budgetMax', 'Budget Max')}><Input type="number" /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="preferred_locations" label={t('office.preferredAreas', 'Preferred Areas')}><Input placeholder="Pathum Wan, Sukhumvit" /></Form.Item></Col>
              <Col span={12}><Form.Item name="boi_status" label="BOI"><Select allowClear><Select.Option value="none">{t('common.none', 'None')}</Select.Option><Select.Option value="applied">{t('office.boiApplied', 'Applied')}</Select.Option><Select.Option value="approved">{t('office.boiApproved', 'Approved')}</Select.Option></Select></Form.Item></Col>
            </Row>
            <Form.Item name="notes" label={t('office.reqNotes', 'Requirement Notes')}><Input.TextArea rows={2} /></Form.Item>
            <Space>
              <Button onClick={() => setCrmAddMode(false)}>← {t('office.backToList', 'Back to Case List')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={createLeadWithProp}>{t('office.createAndLink', 'Create & Link Property')}</Button>
            </Space>
          </Form>
        )}
      </Modal>
    </div>
  );
}
